import { useState, useMemo, useEffect, useCallback } from 'react';

import { Client, gql, fetchExchange, mapExchange, // cacheExchange
	useMutation as _useMutation,
	useQuery as _useQuery,
} from 'urql';

import config from '../config';

const GQL = {
	signup: gql`
		mutation Signup(
			$type: AuthTypeInput!,
			$credentials: AuthCredentialsInput!,
			$firstName: String, $lastName: String,
			$referrer: String,
			$promotionCode: String,
			$stripePriceId: String,
		) {
			signup(
				type: $type,
				credentials: $credentials,
				firstName: $firstName, lastName: $lastName,
				referrer: $referrer,
				promotionCode: $promotionCode,
				stripePriceId: $stripePriceId,
			) {
				id
				group
				email
				firstName
				lastName
			}
		}
	`,
	signin: `
		mutation Signin($type: AuthTypeInput!, $credentials: AuthCredentialsInput!) {
			signin(type: $type, credentials: $credentials) {
				token
				expires
			}
		}
	`,
	signout: gql`
		mutation Signout {
			signout
		}
	`,
	user: gql`
		query User {
			user {
				id
				group
				email
				firstName
				lastName

				reamazeAuthKey
			}
		}
	`,
	userUpdate: gql`
		mutation UserUpdate(
			$email: String,
			$firstName: String,
			$lastName: String,
		) {
			userUpdate(
				email: $email,
				firstName: $firstName,
				lastName: $lastName,
			) {
				id
				group
				email
				firstName
				lastName
			}
		}
	`,
	userPasscode: gql`
		query UserPasscode {
			userPasscode {
				secret
				uri
				qr
			}
		}
	`,
	userPasscodeUpdate: gql`
		mutation UserPasscodeUpdate($secret: String!, $passcode: String!) {
			userPasscodeUpdate(secret: $secret, passcode: $passcode) {
				id
				group
				email
				firstName
				lastName
			}
		}
	`,
	// reserved
	// userPasswordUpdate: gql`
	//  mutation UserPasswordUpdate($token: String!, $password: String!, $passcode: String) {
	//    userPasswordUpdate(token: $token, password: $password, passcode: $passcode)
	//  }
	// `,
	authPasswordEstimate: gql`
		mutation AuthPasswordEstimate($password: String) {
			authPasswordEstimate(password: $password)
		}
	`,
	authPasswordReset: gql`
		mutation AuthPasswordReset($token: String!, $password: String!, $passcode: String) {
			authPasswordReset(token: $token, password: $password, passcode: $passcode) {
				token
				expires
			}
		}
	`,
	authPasswordResetRequest: gql`
		mutation AuthPasswordResetRequest($email: String!) {
			authPasswordResetRequest(email: $email)
		}
	`,
	authEmailConfirm: gql`
		mutation AuthEmailConfirm($token: String!) {
			authEmailConfirm(token: $token) {
				token
				expires
			}
		}
	`,
	account: gql`
		query Account {
			account {
				id
				type
				credits
				recurringCredits
				recurringPeriodCredits
				recurringPeriodEnd
				metadata
			}
		}
	`,
	// obsoleted
	// accountCoupon: gql`
	// 	query AccountCoupon($id: String!) {
	// 		accountCoupon(id: $id) {
	// 			id
	// 			valid
	// 			percentOff
	// 			amountOff
	// 		}
	// 	}
	// `,
	accountInvoice: gql`
		query AccountInvoice($id: String!) {
			accountInvoice(id: $id) {
				id
				invoicePdf
			}
		}
	`,
	accountPaymentSession: gql`
		query AccountPaymentSession {
			accountPaymentSession {
				id
				url
			}
		}
	`,
	accountSignupPaymentSession: gql`
		query AccountSignupPaymentSession($email: String!, $stripePriceId: String!, $successUrl: String, $cancelUrl: String) {
			accountSignupPaymentSession(email: $email, stripePriceId: $stripePriceId, successUrl: $successUrl, cancelUrl: $cancelUrl) {
				id
				url
				clientSecret
			}
		}
	`,
	// obsoleted
	// accountPaymentMethods: gql`
	// 	query AccountPaymentMethodList($type: String) {
	// 		accountPaymentMethods(type: $type) {
	// 			id
	// 			type
	// 			card {
	// 				brand
	// 				country
	// 				last4
	// 			}
	// 		}
	// 	}
	// `,
	action: gql`
		query Action($id: Int!) {
			action(id: $id) {
				id
				code
				address
				metadata
				createdAt
			}
		}
	`,
	// using Float as Long/BigInt, mostly
	actions: gql`
		query Actions($code: String, $createdAtFrom: Float, $search: String, $limit: Int) {
			actions(code: $code, createdAtFrom: $createdAtFrom, search: $search, limit: $limit) {
				id
				code
				address
				metadata
				createdAt
			}
		}
	`,
	// using Float as Long/BigInt, mostly
	actionsOrderCounters: gql`
		query ActionsOrderCounters($isTest: Boolean, $isGroup: Boolean, $createdAtTo: Float!) {
			actionsOrderCounters(isTest: $isTest, isGroup: $isGroup, createdAtTo: $createdAtTo) {
				counter
				counterTest
				hookShortKey
			}
		}
	`,
	// using Float as Long/BigInt, mostly
	actionsOrderStats: gql`
		query ActionsOrderStats($interval: String, $createdAtFrom: Float, $createdAtTo: Float, $providerName: String) {
			actionsOrderStats(interval: $interval, createdAtFrom: $createdAtFrom, createdAtTo: $createdAtTo, providerName: $providerName) {
				counter
				counterError
				createdAt
			}
		}
	`,
	provider: gql`
		query Provider($name: String!) {
			provider(name: $name) {
				name
				title
				status
				authentication
				reference
			}
		}
	`,
	providerMetatraderBranches: gql`
		query ProviderMetatraderBranches($search: String!) {
			providerMetatraderBranches(search: $search)
		}
	`,
	providers: gql`
		query Providers {
			providers
		}
	`,
	// obsoleted
	// providers: gql`
	//  query Providers {
	//    providers {
	//      name
	//      title
	//      status
	//      reference
	//      authentication {
	//        type
	//        credentials
	//        referrerBase
	//        redirectUrl
	//        help
	//      }
	//    }
	//  }
	// `,
	partner: gql`
		query Partner($name: String!) {
			partner(name: $name) {
				name
				title
			}
		}
	`,
	connector: gql`
		query Connector($id: Int) {
			connector(id: $id) {
				id
				providerName
				accounts
				branch
				status
				isActive
				isDemo
				expiresAt
			}
		}
	`,
	connectors: gql`
		query Connectors {
			connectors {
				id
				providerName
				accounts
				branch
				status
				isActive
				isDemo
				expiresAt
			}
		}
	`,
	connectorCreate: gql`
		mutation ConnectorCreate($providerName: String!) {
			connectorCreate(providerName: $providerName) {
				type
				credentials
				referrerBase
				requestUrl
				redirectUrl
				help
				metadata
			}
		}
	`,
	connectorActivate: gql`
		mutation ConnectorActivate($providerName: String!, $credentials: [String]) {
			connectorActivate(providerName: $providerName, credentials: $credentials) {
				id
				providerName
				accounts
				status
				isActive
			}
		}
	`,
	connectorUpdate: gql`
		mutation ConnectorUpdate($id: Int!, $accounts: JSON) {
			connectorUpdate(id: $id, accounts: $accounts) {
				id
				providerName
				accounts
			}
		}
	`,
	connectorDelete: gql`
		mutation ConnectorDelete($id: Int!) {
			connectorDelete(id: $id) {
				id
			}
		}
	`,
	hook: gql`
		query Hook($id: Int!) {
			hook(id: $id) {
				id
				key
				shortKey
				name
				accountId
				isActive
				isTest
				connector {
					id
					providerName
					accounts
					status
					isActive
				}
			}
		}
	`,
	hooks: gql`
		query Hooks($connectorId: Int) {
			hooks(connectorId: $connectorId) {
				id
				key
				shortKey
				name
				accountId
				isActive
				isTest
				connector {
					id
					providerName
					accounts
					status
					isActive
				}
			}
		}
	`,
	hookCreate: gql`
		mutation HookCreate($connectorId: Int!, $accountId: String, $name: String, $isTest: Boolean) {
			hookCreate(connectorId: $connectorId, accountId: $accountId, name: $name, isTest: $isTest) {
				id
				key
				shortKey
				name
				accountId
				isActive
				isTest
			}
		}
	`,
	hookUpdate: gql`
		mutation HookUpdate($id: Int!, $name: String) {
			hookUpdate(id: $id, name: $name) {
				id
				key
				shortKey
				accountId
				isActive
				isTest
				name
			}
		}
	`,
	hookDelete: gql`
		mutation HookDelete($id: Int!) {
			hookDelete(id: $id) {
				id
			}
		}
	`,
	notification: gql`
		query Notifications($id: Int!) {
			notification(id: $id) {
				id
				type
				metadata
			}
		}
	`,
	notifications: gql`
		query Notifications($connectorId: Int!, $accountId: String) {
			notifications(connectorId: $connectorId, accountId: $accountId) {
				id
				type
				metadata
			}
		}
	`,
	notificationCreate: gql`
		mutation NotificationCreate($connectorId: Int!, $accountId: String, $type: Int!, $metadata: JSON) {
			notificationCreate(connectorId: $connectorId, accountId: $accountId, type: $type, metadata: $metadata) {
				id
				type
				metadata
			}
		}
	`,
	notificationUpdate: gql`
		mutation NotificationUpdate($id: Int!, $metadata: JSON) {
			notificationUpdate(id: $id, metadata: $metadata) {
				id
				type
				metadata
			}
		}
	`,
	notificationDelete: gql`
		mutation NotificationDelete($id: Int!) {
			notificationDelete(id: $id) {
				id
				type
				metadata
			}
		}
	`,
	orderPreview: gql`
		mutation OrderPreview($connectorId: Int!, $accountId: String, $data: String!) {
			orderPreview(connectorId: $connectorId, accountId: $accountId, data: $data) {
				id,
				symbol,
				action,
				quantity,
				price,
				status,
				statusDescription,
			}
		}
	`,
	product: gql`
		query Product($id: Int!) {
			product(id: $id) {
				id
				type
				name
				credits
				price
				metadata
			}
		}
	`,
	products: gql`
		query Products {
			products {
				id
				type
				name
				credits
				price
				metadata
			}
		}
	`,
	adminReferrerCreate: gql`
		mutation AdminReferrerCreate($name: String, $providerName: String, $credits: Int!) {
			adminReferrerCreate(name: $name, providerName: $providerName, credits: $credits)
		}
	`,
	adminReferrerDelete: gql`
		mutation AdminReferrerDelete($referrer: String!) {
			adminReferrerDelete(referrer: $referrer)
		}
	`,
	adminAccountReward: gql`
		mutation AdminAccountReward($email: String!, $credits: Int!) {
			adminAccountReward(email: $email, credits: $credits)
		}
	`,
	adminReferrerStatsReport: gql`
		query AdminReferrerStatsReport {
			adminReferrerStatsReport
		}
	`,
	// obsoleted
	// adminUserStatsReport: gql`
	//  query AdminUserStatsReport {
	//    adminUserStatsReport
	//  }
	// `,
	// obsoleted
	// adminProviderStatsReport: gql`
	//  query AdminProviderStatsReport {
	//    adminProviderStatsReport
	//  }
	// `,
};

function getHeaders() {
	const token = localStorage.getItem('token'); // to config?
	const proxiedUserEmail = sessionStorage.getItem('proxiedUserEmail'); // to config?

	return {
		...(proxiedUserEmail && {'X-User': proxiedUserEmail}),
		'Authorization': token ? `Bearer ${token}` : '',
	};
}

function getOptions(optionsOrController, controllerOrUndefined) {
	const controller = optionsOrController instanceof AbortController ?
		optionsOrController : controllerOrUndefined;

	const context = useMemo(() => ({}), [controller]); // critical: useMemo

	context.fetchOptions = {
		headers: getHeaders(),
		...(controller && {signal: controller.signal}),
	};

	// weak: using "memoed" context seems week... or not? no sides?
	return {...optionsOrController, context};
}

export function useQuery(gqlOrName, optionsOrController, controllerOrUndefined) {
	return _useQuery({
		query: GQL[gqlOrName] || gqlOrName,
		...getOptions(optionsOrController, controllerOrUndefined)
	});
}

export function useLazyQuery(gqlOrName, optionsOrController, controllerOrUndefined) {
	// based on
	// https://github.com/urql-graphql/urql/issues/513#issuecomment-980511538

	const [ state, setState ] = useState({
		// resolve
		// reject
		// nonce: execution id
		// variables: execution variables
		// args: execution ...args
	});

	const [ result, _execute ] = _useQuery({
		query: GQL[gqlOrName] || gqlOrName,
		...getOptions(optionsOrController, controllerOrUndefined),
		...(state.variables && {variables: state.variables}),
		pause: true,
	});

	useEffect(() => {
		state.nonce && _execute(...(state.args ?? []));
	}, [state.nonce]);

	useEffect(() => {
		// weak / exprimental
		if (!result.fetching && state.prevResult?.fetching && state.resolve) {
			const { resolve, reject } = state;

			if (result.error) {
				reject(result.error.networkError || result.error.graphQLErrors?.[0]);
			}

			resolve(result);
			setState(s => ({...s, resolve: undefined, reject: undefined}));
		}

		setState(s => ({...s, prevResult: result}));
	}, [result]);

	const execute = useCallback((variables, ...args) => {
		return new Promise((resolve, reject) => {
			setState(s => ({...s, resolve, reject,
				nonce: Date.now(),
				variables,
				args,
			}));
		});
	}, []);

	// TODO apollo: drop backwards compatibility
	return [execute, result];
}

export function useMutation(gqlOrName, optionsOrController, controllerOrUndefined) {
	const [ result, _execute ] = _useMutation(
		GQL[gqlOrName] || gqlOrName,
		// TODO how to make it work? / getOptions(optionsOrController, controllerOrUndefined)
	);

	const execute = useCallback((variables, ...args) => {
		return new Promise((resolve, reject) => {
			_execute(variables, ...args).then(_result => {
				if (_result.error) {
					reject(_result.error.networkError || _result.error.graphQLErrors?.[0]);
				}

				resolve(_result);
			});
		});
	}, []);

	// TODO apollo: drop backwards compatibility
	return [execute, result];
}

export const graph = new (class Graph {
	constructor(url) {
		// TODO add 'X-Remote-Address'?
		this.client = new Client({
			url,
			exchanges: [
				// reserved / cacheExchange,
				mapExchange({
					// weak: wait for a nice scalarSomethingExchange
					onResult(result) {
						const parse = item =>
							Object.assign(item, {
								...(item.accounts && {accounts: JSON.parse(item.accounts || '[]')}),
								...(item.metadata && {metadata: JSON.parse(item.metadata || '{}')}),
							});

						result.data?.connectors &&
							result.data.connectors.forEach(parse);

						result.data?.connector &&
							parse(result.data.connector);

						result.data?.notifications &&
							result.data.notifications.forEach(parse);

						return result;
					},
					onError({ graphQLErrors, httpError }) {
						if (graphQLErrors) {
							for (const err of graphQLErrors) {

								switch (err.extensions?.code) {
									case 'UNAUTHENTICATED':
										this.onUnauthenticatedError?.(); // weak
										break;
								}
							}
						}

						if (httpError) {
							console.log(`Network error: ${httpError}`); // temporal?
						}
					},
				}),
				fetchExchange,
			],
			fetchOptions: () => ({
				headers: getHeaders(),
			}),
		});
	}
})(config.server.baseGraph);

// temporal, 240220: prototype SSE requests
// (async () => {
//  const connectorId = 256;

//  const response = await fetch(config.server.base + '/api/subscriptions', {
//    body: JSON.stringify({connectorId}),
//    headers: {
//      'Content-Type': 'application/json',
//      'Authorization': 'Bearer ' + localStorage.getItem('token'),
//    },
//    method: 'post',
//  });

//  const { token } = await response.json();

//  const evtSource = new EventSource(config.server.base + '/api/subscriptions/' + token, {
//    withCredentials: true,
//    headers: {'Authorization': 'Bearer TEST'},
//  });

//  evtSource.onmessage = event => {
//  };
// })();

export default graph;
